import React, { useState, useEffect, useCallback } from 'react';
import { applicationsAPI, siteConfigAPI } from '../services/api';
import { Button } from '../components/ui/button';
import SubmissionSuccessDialog from '../components/SubmissionSuccessDialog';

const ContactPage = () => {
  const [config, setConfig] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    record_type: 'General Inquiry'
  });
  const [submitting, setSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const data = await siteConfigAPI.get();
      setConfig(data);
    } catch (err) {
      console.error('Error loading contact info:', err);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleWhatsAppClick = () => {
    if (!config?.whatsapp) return;
    window.open(`https://wa.me/${config.whatsapp}?text=Hello%2C%20I%20want%20to%20contact%20you`, '_blank');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await applicationsAPI.create(formData);
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
        record_type: 'General Inquiry'
      });
      setSuccessOpen(true);
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-32 pb-16">
      <SubmissionSuccessDialog
        open={successOpen}
        onOpenChange={setSuccessOpen}
        title="Thank you for your message"
        description="We have received your details successfully. Our team will review your message and get back to you soon."
        nextStep="For urgent questions, you can also reach us on WhatsApp during office hours."
        buttonText="Close"
      />
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold text-red-700 mb-8">
            Contact Us
          </h1>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Contact Information */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Get in Touch</h2>
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Email</h3>
                  {config?.email ? (
                    <a href={`mailto:${config.email}`} data-testid="contact-email-link" className="text-red-700 hover:text-red-800 text-lg">
                      {config.email}
                    </a>
                  ) : (
                    <p className="text-gray-400 text-lg">Loading…</p>
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">WhatsApp</h3>
                  <p data-testid="contact-whatsapp" className="text-gray-700 text-lg">
                    {config?.whatsapp ? `+${config.whatsapp}` : 'Loading…'}
                  </p>
                </div>
                <Button 
                  onClick={handleWhatsAppClick}
                  disabled={!config?.whatsapp}
                  className="w-full bg-green-500 hover:bg-green-600 text-white"
                >
                  Contact via WhatsApp
                </Button>
              </div>
              
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Office Hours</h3>
                <div className="space-y-1 text-gray-700">
                  <p>Monday - Friday: 10:00 AM - 6:00 PM</p>
                  <p>Saturday: 10:00 AM - 2:00 PM</p>
                  <p>Sunday: Closed</p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Send us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="5"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="How can we help you?"
                    required
                  ></textarea>
                </div>

                <Button 
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-red-700 hover:bg-red-800 text-white py-3"
                >
                  {submitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
